import React, { useState, useEffect } from 'react';
import '../../spinner.css';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getUserDetails } from '../../services/UserService';
import { getCourseConsumedReportData, getCourseReportData } from '../../services/EvaluationService';
import { Chart, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import Loader from '../../utility/Loader';
import { downloadCsvWithCustomHeaders } from '../../utility/downloadReport';

Chart.register(ArcElement, Tooltip, Legend, Title);

export default function LearningReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courseConsumedReport, setCourseConsumedReport] = useState({});
  const [overviewReport, setOverviewReport] = useState({});
  const [courseReport, setCourseReport] = useState([]);
  const [colors, setColors] = useState(['Red', 'Blue', 'Yellow', 'Green', 'Pink', 'Orange', 'Grey', 'Violet', 'Turquoise']);
  const [data, setData] = useState({
    labels: [],
    datasets: [{
      label: 'Dataset',
      data: [],
      backgroundColor: [],
      hoverOffset: 4
    }]
  });

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            const index = context.dataIndex;
            let course_name = courseReport[index].course_name;
            let module_count = courseReport[index].num_of_modules;
            let total_comp_std = courseReport[index].users_completed;
            let total_learning_time = courseReport[index].learning_sec;
            let total_std = courseReport[index].users_enrolled;
            return [course_name, module_count + " Modules", total_std + " Students", total_comp_std + " Students Completed", Math.ceil(total_learning_time / 60) + " Minutes Spent"]
          },
          title: function () {
            return 'Detailed Data';
          }
        },
        useHTML: true
      }
    }
  };

  useEffect(() => {
    const userDetails = getUserDetails();
    if (userDetails.token === undefined) {
      navigate('/login');
    } else {
      getAllData(userDetails.token);
      Chart.defaults.plugins.tooltip.backgroundColor = 'rgb(0, 0, 156)';
      Chart.defaults.plugins.legend.position = 'bottom';
      Chart.defaults.plugins.legend.title.display = true;
      Chart.defaults.plugins.legend.title.text = 'Learning Data';
      Chart.defaults.plugins.legend.title.font = 'Helvetica Neue';

    }
  }, []);
  const getAllData = async (token) => {
    setLoading(true);
    await getCourseReport(token);
    await getCourseConsumedReport(token);
    setLoading(false);
  };

  const getCourseReport = async (token) => {
    let resp = await getCourseReportData(token);
    if (resp.error === false) {
      setCourseReport(resp.data.course_res);
      let chartData = resp.data.course_res;
      let labels = [];
      let dataValues = [];
      let bgColors = [];
      let count_time_spent = 0;
      let count_course_completed = 0;
      let count_modules_completed = 0;
      let count_tests_completed = 0;
      for (let i = 0; i < chartData.length; i++) {
        if (chartData[i].learning_sec !== undefined && chartData[i].learning_sec !== null) {
          labels.push(chartData[i].course_name);
          dataValues.push(parseInt(chartData[i].learning_sec));
          bgColors.push(colors[i]);
          count_time_spent = count_time_spent + parseInt(chartData[i].learning_sec);
          if (chartData[i].users_completed === chartData[i].users_enrolled) {
            count_course_completed = count_course_completed + 1;
          }
        }

      }
      if (resp.data.module_res !== undefined && resp.data.module_res !== null && resp.data.module_res.length > 0) {
        count_modules_completed = resp.data.module_res[0].modules_completed !== undefined && resp.data.module_res[0].modules_completed !== null ? resp.data.module_res[0].modules_completed : 0;
        count_tests_completed = resp.data.module_res[0].tests_completed !== undefined && resp.data.module_res[0].tests_completed !== null ? resp.data.module_res[0].tests_completed : 0;
      }
      setOverviewReport({ count_time_spent: Math.ceil(count_time_spent / 60), count_course_completed: count_course_completed, count_modules_completed: count_modules_completed, count_tests_completed: count_tests_completed })
      let newData = {
        labels: labels,
        datasets: [{
          label: 'Dataset',
          data: dataValues,
          backgroundColor: bgColors,
          hoverOffset: 4
        }]
      };
      setData(newData);
    }
  };

  const getCourseConsumedReport = async (token) => {
    let resp = await getCourseConsumedReportData(token);
    if (resp.error === false) {
      setCourseConsumedReport(resp.data);
    }
  };

  const handleDownloadReport = () => {
    if (courseReport.length === 0) {
      toast.error('No data available to download');
      return;
    }
    // Format the course report data for CSV
    const formattedData = courseReport.map(course => ({
      course_name: course.course_name,
      num_of_modules: course.num_of_modules,
      users_enrolled: course.users_enrolled,
      users_completed: course.users_completed,
      learning_sec: course.learning_sec,
      learning_time_minutes: Math.ceil(course.learning_sec / 60),
      completion_rate: course.users_enrolled > 0 ? Math.round((course.users_completed / course.users_enrolled) * 100) : 0
    }));

    // Custom header mapping for better column names
    const headerMapping = {
      'Course Name': 'course_name',
      'Number of Modules': 'num_of_modules',
      'Users Enrolled': 'users_enrolled',
      'Users Completed': 'users_completed',
      'Learning Time (Seconds)': 'learning_sec',
      'Learning Time (Minutes)': 'learning_time_minutes',
      'Completion Rate (%)': 'completion_rate'
    };

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `learning-report-${timestamp}.csv`;

    downloadCsvWithCustomHeaders(formattedData, headerMapping, filename);
    toast.success('Learning report downloaded successfully');
  };


  return (
    <div>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className='pt-10 lg:px-12 px-6 pb-12'>
            <h3 className='text-2xl text-secondary text-center inter-700 lg:hidden mb-4'>Learning Report</h3>
            <div className='back-btn flex justify-between items-center mb-5 md:mb-8'>
              <a onClick={() => navigate('/admin/evaluation')} className='cursor-pointer w-auto bg-tertiary hover:bg-black flex items-center justify-center px-6 h-7 2xl:h-8 text-white inter-500 text-xs 2xl:text-lg rounded-3xl lg:rounded-full transition duration-300 button-shadow'>Back</a>
              <a onClick={handleDownloadReport} className='cursor-pointer bg-primary hover:bg-black flex items-center justify-center px-6 h-10 2xl:h-12 text-white inter-600 text-base md:text-sm lg:text-base 2xl:text-lg rounded-3xl lg:rounded-full transition duration-300 button-shadow'>Download Report</a>
            </div>
            <div className='report-card-row flex'>
              <div className='w-full'>
                <div className='cohort-big-card rounded-3xl lg:p-10 p-4 md:p-8 mb-8'>
                  <h2 className='text-3xl text-white inter-700 mb-4'>Summary</h2>
                  <div className='flex items-center flex-wrap'>
                    <div className='w-full md:w-1/2'>
                      <div className='cohort-big-card-left'>
                        <div className='mb-5 md:mb-6'>
                          <h3 className='text-2xl text-white inter-700'>{courseConsumedReport.max_course_name} | {Math.ceil(courseConsumedReport.max_per)}%</h3>
                          <p className='text-base 2xl:text-lg text-white inter-400'>Most Consumed Technology</p>
                        </div>
                        <div className=''>
                          <h3 className='text-2xl text-white inter-700'>{courseConsumedReport.min_course_name} | {Math.ceil(courseConsumedReport.min_per)}%</h3>
                          <p className='text-base 2xl:text-lg text-white inter-400'>Least Consumed Technology </p>
                        </div>
                      </div>
                    </div>
                    <div className='w-full md:w-1/2 mt-5 md:mt-0'>
                      <div className='cohort-big-card-right'>
                        <p className='text-xl text-white inter-400 mb-2 md:mb-3'><span className='inter-700'>{overviewReport.count_course_completed}</span> Technology Completed</p>
                        <p className='text-xl text-white inter-400 mb-2 md:mb-3'><span className='inter-700'>{overviewReport.count_modules_completed}</span> Modules Completed </p>
                        <p className='text-xl text-white inter-400 mb-2 md:mb-3'><span className='inter-700'>{overviewReport.count_tests_completed}</span> Tests Completed </p>
                        <p className='text-xl text-white inter-400 md:mb-0'><span className='inter-700'>{overviewReport.count_time_spent}</span> Minutes Spent </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='report-graph flex justify-center'>
                  <div className='w-full lg:w-2/5 bg-[#F1EAEA] rounded-3xl p-6'>
                    <Doughnut data={data} options={options} />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </>
      )
      }
    </div>
  );
}